const GRAMS_PER_LB = 453.59237;
const CLOTHING_TYPES = new Set(['apparel', 'clothing', 'clothes', 'mixed brand clothes', 't-shirt', 't-shirts', 'tee', 'tees', 'shirt', 'shirts', 'top', 'tops', 'jacket', 'jackets', 'hoodie', 'hoodies', 'sweater', 'sweaters', 'cardigan', 'cardigans', 'pants', 'jeans', 'denim', 'shorts', 'skirt', 'skirts', 'dress', 'dresses', 'flannel', 'flannels', 'sweats', 'sweatpants', 'sportswear']);
// Native Ajax cart items omit product tags, and translated product_type is display
// copy. These existing wholesale catalog identities stay stable in every locale.
const WHOLESALE_CATALOG = new Map([
  ['9400273961180', 'band-t-shirts'],
  ['9385234366684', 'nike-jackets-and-hoodies'],
  ['9385225453788', 'leather-jackets'],
  ['9381328257244', 'baby-kids-vintage'],
  ['9381326455004', 'sequin-beaded-dresses-tops'],
  ['9381323571420', 'mens-y2k-mix'],
  ['9381322227932', 'womens-y2k-mix'],
  ['9370917208284', 'polo-tommy-hilfiger-and-nautica'],
  ['9370904363228', 'abercrombie-bottoms-copy'],
  ['9286012305628', 'north-face-vintage-jackets'],
  ['9361183899868', 'cargo-pants'],
  ['9361180164316', 'mens-mixese'],
  ['9361175347420', 'abercrombie-hollister-aeropostale-and-american-eagle'],
  ['9361132191964', 'womens-tops'],
  ['9361124393180', 'vintage-patterned-shirts-copy'],
  ['9286068764892', 'womens-vintage'],
  ['9286054412508', 'vintage-skirts'],
  ['9286026166492', 'vintage-flannels'],
  ['9283319202012', 'levi-s-vintage-jeans'],
  ['9283267690716', 'cartoon-character-vintage-t-shirts'],
  ['9283174138076', 'pro-sports-vintage-t-shirts'],
  ['9271029825756', 'vintage-graphic-t-shirts']
]);
const WHOLESALE_HANDLES = new Set(WHOLESALE_CATALOG.values());
const EXCLUDED_PRODUCT_IDS = new Set(['15447186079964', '9385316483292', '9385310650588', '9381319540956']);
const EXCLUDED_PRODUCT_HANDLES = new Set(['grail-premium-vintage-t-shirts']);
const EXCLUDED_TAGS = new Set(['event', 'ticket', 'appointment', 'custom-bale', 'bale-progress-exclude', 'premier-find', 'premier-finds']);
const EXCLUDED = /(?:fill[-\s]?a[-\s]?bag|custom[-\s]?(?:vintage[-\s]?)?bale|appointment|booking|event[-\s]?ticket|warehouse[-\s]?visit|gift[-\s]?card|grail-premium-vintage-t-shirts)/i;
const positive = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;
const tagsOf = item => {
  const tags = item.product?.tags ?? item.tags ?? [];
  return (Array.isArray(tags) ? tags : String(tags).split(',')).map(tag => String(tag).trim().toLowerCase());
};

export function isBaleProgressEligible(item) {
  if (!item || typeof item !== 'object' || item.gift_card === true) return false;
  const tags = tagsOf(item);
  const productId = String(item.product_id ?? item.product?.id ?? '');
  const type = String(item.product_type ?? item.product?.type ?? item.product?.product_type ?? '').trim().toLowerCase();
  const template = String(item.product?.template_suffix ?? item.template_suffix ?? '').trim().toLowerCase();
  const handle = String(item.handle ?? item.product?.handle ?? '').trim().toLowerCase();
  if (EXCLUDED_PRODUCT_IDS.has(productId) || EXCLUDED_PRODUCT_HANDLES.has(handle) || type === 'premier find' || type === 'premier finds' || ['premier-find', 'premier-finds'].includes(template)) return false;
  const words = [item.handle, item.url, item.product_title, item.product_type, item.product?.handle, item.product?.title, item.product?.type, item.product?.product_type, ...tags].filter(Boolean).join(' ');
  if (EXCLUDED.test(words) || tags.some(tag => EXCLUDED_TAGS.has(tag))) return false;
  if (item.product?.collections?.some(collection => collection.handle === 'fill-a-bag-event')) return false;
  if ((item.requires_shipping ?? item.variant?.requires_shipping) !== true) return false;
  return WHOLESALE_CATALOG.has(productId) || WHOLESALE_HANDLES.has(handle) || CLOTHING_TYPES.has(type) || tags.includes('clothing') || tags.includes('bale-progress-eligible');
}

export function calculateCartBaleProgress(cart = {}, config = {}) {
  const metric = config.metric === 'value' ? 'value' : 'weight';
  const target = metric === 'value' ? positive(config.targetValueCents) : positive(config.targetLbs);
  const currency = String(cart.currency?.iso_code || cart.currency || config.currency || 'USD').toUpperCase();
  const targetCurrency = String(config.currency || 'USD').toUpperCase();
  const eligible = (Array.isArray(cart.items) ? cart.items : []).filter(item => isBaleProgressEligible(item) && Number.isSafeInteger(Number(item.quantity)) && Number(item.quantity) > 0);
  let current = 0;
  let unweighedCount = 0;
  for (const item of eligible) {
    if (metric === 'value') {
      // Shopify's final line total already includes quantity and allocated discounts.
      current += Math.max(0, Number.isFinite(Number(item.final_line_price)) ? Number(item.final_line_price) : 0);
    } else {
      // Use declared Shopify grams as an approximation, never an inferred piece weight.
      // A merchant's explicit clothing-weight metafield may override configured grams.
      const id = String(item.variant_id ?? item.variant?.id ?? item.id ?? '');
      const grams = positive(config.verifiedWeights?.[id]) || positive(item.grams ?? item.variant?.weight ?? item.variant?.grams);
      if (grams) current += grams / GRAMS_PER_LB * Number(item.quantity);
      else unweighedCount += Number(item.quantity);
    }
  }
  const available = target > 0 && (metric === 'weight' ? current > 0 : currency === targetCurrency);
  // Shopify stores whole grams: a declared100lb variant is45359g, not45359.237g.
  const complete = available && current >= target - (metric === 'weight' ? .5 / GRAMS_PER_LB : 1e-8);
  const percent = available ? complete ? 100 : Math.min(100, Math.floor(current / target * 100 + 1e-8)) : 0;
  return { metric, currency, targetCurrency, target, current, remaining: complete ? 0 : Math.max(0, target - current), percent, complete, available, visible: eligible.length > 0, qualifyingLines: eligible.length, unweighedCount, partialWeight: metric === 'weight' && unweighedCount > 0 && current > 0 };
}

const COPY = {
  heading_weight: 'Approx. clothing weight', heading_value: 'Clothing budget comparison',
  detail_weight: '{current} / {target} lb · {remaining} lb away', detail_value: '{current} / {target} in clothing',
  unavailable_weight: 'Weight unavailable for these items.', unavailable_value: 'Compare clothing totals in {currency}.',
  note: 'Build A Bale is a separate purchase.', partial_note: 'Unweighed bundles not counted: {count}.',
  at_least: 'At least {percent}%', percent: '{percent}%', unavailable: '—', value_unavailable: 'Progress unavailable',
  cta: 'Explore Build A Bale ↗', cta_complete: 'Choose your custom mix ↗', aria_value: '{percent}. {detail}. {note}'
};
const translate = (translations, key, values = {}) => String(translations[key] || COPY[key] || key).replace(/\{(\w+)\}/g, (match, token) => values[token] ?? match);

export function describeCartBaleProgress(state, locale = 'en-US', translations = {}) {
  const number = amount => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(amount);
  const money = cents => new Intl.NumberFormat(locale, { style: 'currency', currency: state.targetCurrency, maximumFractionDigits: 2 }).format(cents / 100);
  const t = (key, values) => translate(translations, key, values);
  const heading = t('heading_' + state.metric);
  const cta = t(state.complete ? 'cta_complete' : 'cta');
  if (!state.available) return { heading, cta, percent: t('unavailable'), detail: t('unavailable_' + state.metric, { currency: state.targetCurrency }), note: t('note'), valueText: t('value_unavailable') };
  const detail = t('detail_' + state.metric, { current: state.metric === 'value' ? money(state.current) : number(state.current), target: state.metric === 'value' ? money(state.target) : number(state.target), remaining: number(state.remaining) });
  const percent = t(state.partialWeight ? 'at_least' : 'percent', { percent: state.percent });
  const note = state.partialWeight ? t('partial_note', { count: state.unweighedCount }) : t('note');
  return { heading, cta, percent, detail, note, valueText: t('aria_value', { percent, detail, note }) };
}

export function renderCartBaleProgress(node, cart, config) {
  const state = calculateCartBaleProgress(cart, config);
  node.hidden = !state.visible;
  if (!state.visible) return state;
  const copy = describeCartBaleProgress(state, config.locale || 'en-US', config.translations);
  const bar = node.querySelector('[data-bale-progress-bar]');
  const fill = node.querySelector('[data-bale-progress-fill]');
  const percent = node.querySelector('[data-bale-progress-percent]');
  const detail = node.querySelector('[data-bale-progress-detail]');
  const note = node.querySelector('[data-bale-progress-note]');
  const heading = node.querySelector('[data-bale-progress-heading]');
  const cta = node.querySelector('[data-bale-progress-cta]');
  if (bar) {
    bar.hidden = !state.available;
    bar.setAttribute('aria-valuenow', String(state.percent));
    bar.setAttribute('aria-valuetext', copy.valueText);
  }
  if (fill) fill.style.width = `${state.percent}%`;
  if (percent) percent.textContent = copy.percent;
  if (detail) detail.textContent = copy.detail;
  if (note) note.textContent = copy.note;
  if (heading) heading.textContent = copy.heading;
  if (cta) cta.textContent = copy.cta;
  node.dataset.complete = String(state.complete);
  return state;
}

export function attachCartBaleProgress(root, currentCart = null) {
  const components = [];
  root.querySelectorAll('[data-cart-bale-progress]').forEach(node => {
    try {
      const data = node.querySelector('[data-bale-progress-data]');
      const payload = JSON.parse(data.content?.textContent || data.textContent);
      components.push({ node, config: payload.config });
      renderCartBaleProgress(node, currentCart || payload.cart, payload.config);
    } catch (_) { node.hidden = true; }
  });
  const update = event => {
    const cart = event.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    components.forEach(({ node, config }) => renderCartBaleProgress(node, cart, config));
  };
  root.addEventListener('nlv:cart-updated', update);
  return () => root.removeEventListener('nlv:cart-updated', update);
}

if (typeof document !== 'undefined') {
  let currentCart = null;
  document.addEventListener('nlv:cart-updated', event => {
    if (Array.isArray(event.detail?.cart?.items)) currentCart = event.detail.cart;
  });
  let detach = attachCartBaleProgress(document);
  document.addEventListener('shopify:section:load', () => { detach(); detach = attachCartBaleProgress(document, currentCart); });
}
